import React, { useState, useCallback } from 'react';

import { useMemberRecommendations, useUpgradeProduct, useDismissRecommendation } from '../hooks/crossSell/useCrossSell';
import { notifyUser } from '../util/util';
function RecommendUpgrade({ 
  onUpgrade,
  onNotInterested
}) {
  const [isVisible, setIsVisible] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const formatCurrency = (value) => {
    return `GHS ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const { data: recommendationsResult, isLoading } = useMemberRecommendations();
  console.log('Upsell recommendations:', recommendationsResult);
  const upgradeMutation = useUpgradeProduct();
  const dismissMutation = useDismissRecommendation();

  const recommendations = recommendationsResult?.data || [];
  const recommendation = recommendations[currentIndex];

  const nextRecommendation = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % recommendations.length);
  }, [recommendations.length]);

  const prevRecommendation = () => {
    setCurrentIndex((prev) => (prev - 1 + recommendations.length) % recommendations.length);
  };

  // Auto-slide every 5 seconds
  React.useEffect(() => {
    if (recommendations.length <= 1) return;
    const interval = setInterval(nextRecommendation, 5000);
    return () => clearInterval(interval);
  }, [recommendations.length, nextRecommendation]);

  // Don't render if no recommendations from API
  if (isLoading) {
    return null;
  }

  if (!recommendation || recommendations.length === 0) {
    return null;
  }

  const currentPremium = Number(recommendation.current_premium);
  const suggestedPremium = Number(recommendation.recommended_premium);
  const additionalBenefit = Number(recommendation.new_sum_assured) - currentPremium;


 
  if (!isVisible) {
    return null;
  }

  return (
    <div className="recommend-upgrade" style={{
      padding: '32px',
      background: '#fff',
      borderRadius: '18px',
      boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)',
      maxWidth: '700px',
      width: '100%'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <h2 className="recommend-title">
          Recommended Premium Upgrade {recommendation ? `for ${recommendation.policy_number}` : ''}
        </h2>
        {recommendations.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button 
              onClick={prevRecommendation}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px'
              }}
            >
              ‹
            </button>
            <span style={{ fontSize: '14px', color: '#666' }}>
              {currentIndex + 1} of {recommendations.length}
            </span>
            <button 
              onClick={nextRecommendation}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px'
              }}
            >
              ›
            </button>
          </div>
        )}
      </div>
      <p className="recommend-subtitle">Increase your coverage today</p>

      {/* Premium Cards */}
      <div className="premium-cards">
        {/* Current Premium Card */}
        <div className="premium-card">
          <div style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>Current Premium</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#000' }}>{formatCurrency(currentPremium)}</div>
        </div>

        {/* Suggested Premium Card */}
        <div className="premium-card">
          <div style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>Suggested Premium</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#000' }}>{formatCurrency(suggestedPremium)}</div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="benefit-box">
        <div className="benefit-title">New Coverage Benefit (Sum Assured)</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <span className="benefit-value">{formatCurrency(recommendation ? recommendation.new_sum_assured : suggestedPremium)}</span>
          <span className="benefit-old">{formatCurrency(currentPremium)}</span>
        </div>
        
        {/* Additional Benefit */}
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '16px'
        }}>
          <span style={{ 
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            background: '#fff',
            borderRadius: '50%'
          }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M4.5 8.5L2 6L1 7L4.5 10.5L11 4L10 3L4.5 8.5Z" fill="#00C853"/>
            </svg>
          </span>
          <span className="additional-advantage">{formatCurrency(additionalBenefit)} additional advantage</span>
        </div>
      </div>

  

      {/* Action Buttons */}
      <div className="action-buttons">
        <button 
          className="btn btn-primary"
          style={{ flex: 1, fontWeight: '600' }}
          onClick={async () => {
            if (upgradeMutation.isPending) return; // Prevent multiple clicks
            
            if (recommendations.length > 0) {
              try {
                await upgradeMutation.mutateAsync(recommendations[currentIndex].id);
                notifyUser('success', 'Product upgraded successfully', 'Success');
                // Only call onUpgrade after successful upgrade
                onUpgrade?.();
              } catch (err) {
                notifyUser('error', err?.message || 'Failed to upgrade product', 'Error');
              }
            } else {
              // If no recommendations, just call onUpgrade
              onUpgrade?.();
            }
          }}
          disabled={upgradeMutation.isPending}
        >
          {upgradeMutation.isPending ? 'Upgrading...' : 'Upgrade Now'}
        </button>
        <button 
          className="btn btn-white"
          style={{ flex: 1, fontWeight: '600' }}
          onClick={() => window.open('https://www.oldmutual.com.gh/', '_blank')}
        >
          Learn More
        </button>
      </div>

      {/* Not Interested Link */}
      <div className="not-interested" style={{ textAlign: 'center' }}>
        <button 
          onClick={async () => {
            try {
              await dismissMutation.mutateAsync(recommendations[currentIndex].id);
              setCurrentIndex((prev) => {
                if (recommendations.length === 1) {
                  setIsVisible(false);
                  onNotInterested?.();
                  return 0;
                }
                return prev >= recommendations.length - 1 ? prev - 1 : prev;
              });
            } catch (err) {
              notifyUser('error', err?.message || 'Failed to dismiss recommendation', 'Error');
            }
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#666',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '8px',
            textDecoration: 'underline'
          }}
        >
          Not interested
        </button>
      </div>

      {/* Dots Indicator */}
      {recommendations.length > 1 && (
        <div className="dots-indicator" style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
          {recommendations.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                border: 'none',
                background: index === currentIndex ? '#00C853' : '#ddd',
                cursor: 'pointer'
              }}
            />
          ))}
        </div>
      )}
      <style>{`
        .recommend-upgrade {
          box-sizing: border-box;
          word-break: break-word;
        }
        .recommend-title {
          line-height: 1.08;
          font-weight: 800;
          margin: 0;
          font-size: clamp(16px, 2.2vw, 24px); /* responsive heading */
        }
        .recommend-subtitle { color: #666; margin-bottom: 24px; font-size: clamp(13px, 1.6vw, 16px); }
        .premium-cards { display: flex; gap: 20px; margin-bottom: 24px; flex-wrap: wrap; }
        .premium-card { flex: 1; padding: 20px; background: #f5f6fa; border-radius: 12px; transition: padding 0.15s ease, font-size 0.15s ease; min-width: 160px; }
        .premium-card div:first-child { color: #666; font-size: clamp(12px, 1.4vw, 14px); margin-bottom: 8px; }
        .premium-card div:nth-child(2) { font-size: clamp(18px, 2.6vw, 24px); font-weight: 700; }

        .benefit-box { background: linear-gradient(135deg, #00C853 0%, #009624 100%); border-radius: 16px; padding: 24px; color: #fff; margin-bottom: 24px; transition: padding 0.15s ease, font-size 0.15s ease; }
        .benefit-title { font-size: clamp(14px, 1.5vw, 18px); font-weight: 600; margin-bottom: 12px; }
        .benefit-value { font-size: clamp(20px, 3.2vw, 28px); font-weight: 700; }
        .benefit-old { text-decoration: line-through; color: rgba(255,255,255,0.8); font-size: clamp(12px, 1.6vw, 18px); }
        .additional-advantage { font-size: clamp(13px, 1.8vw, 16px); }

        .action-buttons { display: flex; gap: 16px; margin-bottom: 16px; }
        .action-buttons button { min-height: 44px; }

        /* Tablet */
        @media (max-width: 900px) {
          .recommend-upgrade { padding: 20px; }
        }

        /* Mobile */
        @media (max-width: 640px) {
          .recommend-upgrade { padding: 12px; border-radius: 12px; }
          .premium-cards { flex-direction: column; gap: 12px; }
          .premium-card { padding: 12px; }
          .benefit-box { padding: 16px; border-radius: 12px; }
          .action-buttons { flex-direction: column; }
          .action-buttons button { font-size: 14px; padding: 10px 12px; }
          .not-interested button { font-size: 13px; padding: 6px; }
          .dots-indicator { margin-top: 12px; }
        }
      `}</style>
    </div>
  );
}

export default RecommendUpgrade;